import { Sparkles, Clock, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { BACKEND_URL } from "../lib/api";
import { useSettings } from "../context/SettingsContext";

/**
 * PackageCard — sized identically to service cards (see ServicesPage.jsx renderCard).
 * Used on /packages, /book (packages section) and landing tab.
 * Bundle vs standalone is inferred from included_service_ids.
 */
export function PackageCard({ pkg, onBook, servicesById = {} }) {
    const { settings } = useSettings();
    const bookable = settings.booking_packages_enabled === true;
    const isBundle = (pkg.included_service_ids || []).length > 0;
    const includedNames = (pkg.included_service_ids || [])
        .map((sid) => servicesById[sid]?.name)
        .filter(Boolean);
    const imgSrc = pkg.image?.startsWith("data:") || pkg.image?.startsWith("http")
        ? pkg.image
        : (pkg.image ? `${BACKEND_URL}${pkg.image}` : null);

    return (
        <div
            className="card-luxe grain group flex flex-col relative"
            data-testid={`package-card-${pkg.id}`}
        >
            {pkg.highlight_text && (
                <div className="absolute top-2 left-0 z-10 bg-[#6B1535] text-[#FFF8F2] text-[0.55rem] tracking-[0.2em] uppercase px-2 py-0.5 font-semibold">
                    {pkg.highlight_text}
                </div>
            )}
            {imgSrc ? (
                <div className="aspect-[16/9] overflow-hidden">
                    <img src={imgSrc} alt={pkg.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
            ) : (
                <div className="aspect-[16/9] bg-[#FDF0EC] flex items-center justify-center">
                    <Sparkles size={26} className="text-[#8F622C]/40" />
                </div>
            )}
            <div className="p-3 flex-1 flex flex-col">
                <div className="flex items-center gap-2">
                    <h3 className="font-serif-display text-base leading-tight text-[#3D0C1F] flex-1">{pkg.name}</h3>
                </div>
                {pkg.category && (
                    <div className="text-[0.55rem] tracking-[0.2em] uppercase text-[#7A5262] mt-0.5">{pkg.category}</div>
                )}
                <p className="text-[0.7rem] text-[#8A6574] mt-1 line-clamp-2 leading-snug">{pkg.description}</p>

                {isBundle && includedNames.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-[#6B1535]/10">
                        <div className="text-[0.55rem] tracking-[0.2em] uppercase text-[#8F622C]">Includes</div>
                        <p className="text-[0.65rem] text-[#7A5262] mt-0.5 line-clamp-2 leading-snug">
                            {includedNames.slice(0, 3).join(" · ")}{includedNames.length > 3 ? ` +${includedNames.length - 3}` : ""}
                        </p>
                    </div>
                )}

                <div className="flex justify-between items-center mt-2 pt-2 border-t border-[#6B1535]/10">
                    <span className="text-[#8F622C] text-sm font-bold">S${pkg.price}</span>
                    <span className="text-[0.65rem] text-[#8A6574] inline-flex items-center gap-1">
                        {pkg.duration_min ? <><Clock size={9}/> {pkg.duration_min} min</> : (isBundle ? "Bundle" : "Standalone")}
                    </span>
                </div>
                {!bookable ? null : onBook ? (
                    <button onClick={() => onBook(pkg)} className="btn-gold mt-2 justify-center py-1.5 text-[0.65rem] font-bold tracking-[0.2em]" data-testid={`book-package-${pkg.id}`}>
                        Book Now <ArrowUpRight size={10} />
                    </button>
                ) : (
                    <Link to={`/book?pkg=${pkg.id}`} className="btn-gold mt-2 justify-center py-1.5 text-[0.65rem] font-bold tracking-[0.2em] inline-flex items-center" data-testid={`book-package-link-${pkg.id}`}>
                        Book Now <ArrowUpRight size={10} />
                    </Link>
                )}
            </div>
        </div>
    );
}
